import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { radiusPx } from '../../../../theme/tokens/radius';
import { shadows } from '../../../../theme/tokens/shadows';

const PLAIN_HEADER_SX = { mb: 2 };

/**
 * Groups related form fields with a titled header.
 * variant="card" — bordered section (License dialog); variant="plain" — onboarding wizard style.
 */
export default function FormSection({
  icon,
  title,
  description,
  children,
  variant = 'card',
}) {
  const { semantic, shadows: themeShadows } = useFeatureTheme();
  const surfaces = semantic.surfaces ?? {};
  const borders = semantic.borders ?? {};
  const text = semantic.text ?? {};
  const interactive = semantic.interactive ?? {};

  if (variant === 'plain') {
    return (
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={PLAIN_HEADER_SX}>
          {icon ? (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: radiusPx.md,
                bgcolor: interactive.primaryMuted ?? interactive.primarySoft,
                color: interactive.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          ) : null}
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {title}
            </Typography>
            {description ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {description}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        {children}
      </Box>
    );
  }

  const sectionShadow = themeShadows?.xs ?? shadows.xs;

  return (
    <Box
      sx={{
        bgcolor: surfaces.surface,
        borderRadius: radiusPx.lg,
        border: `1px solid ${borders.border}`,
        boxShadow: sectionShadow,
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: `1px solid ${borders.border}`,
          bgcolor: surfaces.surfaceSubtle ?? surfaces.surface,
        }}
      >
        {icon ? (
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: radiusPx.md,
              bgcolor: interactive.primaryMuted ?? interactive.primarySoft,
              color: interactive.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: '0.9375rem',
              lineHeight: 1.4,
              color: text.primary,
            }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              variant="body2"
              sx={{
                mt: 0.35,
                fontSize: '0.8125rem',
                lineHeight: 1.45,
                color: text.secondary,
              }}
            >
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      <Box sx={{ px: 2.5, py: 2.25 }}>{children}</Box>
    </Box>
  );
}
